import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Link as MuiLink,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { AppError } from '../../api/contract';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { paths } from '../../routes/paths';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { MetadataList } from '../../shared/detail/MetadataList';
import { formatDecimalString } from '../../shared/format/decimal';
import { walletsRepositoryToken } from './repository';
import type {
  ApprovalRequestSummary,
  WalletAdjustmentInput,
  WalletAdjustmentPreview,
  WalletDetail,
} from './types';
import { compareWalletDecimals } from './types';

interface AdjustmentForm {
  readonly signedAmount: string;
  readonly reasonCode: string;
  readonly evidenceReference: string;
  readonly reason: string;
}

const initialForm: AdjustmentForm = {
  signedAmount: '',
  reasonCode: '',
  evidenceReference: '',
  reason: '',
};

const signedAmountPattern = /^-?(?:0|[1-9][0-9]{0,19})(?:\.[0-9]{1,6})?$/u;
const reasonCodePattern = /^[A-Z][A-Z0-9_]{1,99}$/u;

export function WalletAdjustmentDialog({
  canViewApproval,
  dataSource,
  onClose,
  open,
  wallet,
}: {
  readonly canViewApproval: boolean;
  readonly dataSource: DataSource;
  readonly onClose: () => void;
  readonly open: boolean;
  readonly wallet: WalletDetail;
}) {
  const repository = useRepository(walletsRepositoryToken);
  const { t } = useTranslation();
  const [form, setForm] = useState(initialForm);
  const [preview, setPreview] = useState<WalletAdjustmentPreview | null>(null);
  const [approval, setApproval] = useState<ApprovalRequestSummary | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState<'preview' | 'submit' | null>(null);
  const [error, setError] = useState<AppError | null>(null);
  const idempotencyKeyRef = useRef(crypto.randomUUID());
  const previewingRef = useRef(false);
  const submittingRef = useRef(false);
  const valid = isValid(form);

  const update = (field: keyof AdjustmentForm, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    setPreview(null);
    setApproval(null);
    setConfirmed(false);
    setError(null);
    idempotencyKeyRef.current = crypto.randomUUID();
  };

  const reset = () => {
    setForm(initialForm);
    setPreview(null);
    setApproval(null);
    setConfirmed(false);
    setBusy(null);
    setError(null);
    previewingRef.current = false;
    submittingRef.current = false;
    idempotencyKeyRef.current = crypto.randomUUID();
  };

  const close = () => {
    if (busy !== null) return;
    reset();
    onClose();
  };

  const previewAdjustment = async () => {
    if (!valid || previewingRef.current || busy !== null) return;
    previewingRef.current = true;
    setBusy('preview');
    setError(null);
    try {
      setPreview(await repository.previewAdjustment(wallet.id, normalize(form)));
      setConfirmed(false);
    } catch (cause) {
      setError(toSafeError(cause, t('wallets.adjustment.errors.preview')));
    } finally {
      previewingRef.current = false;
      setBusy(null);
    }
  };

  const submitAdjustment = async () => {
    if (preview === null || !confirmed || submittingRef.current || busy !== null) return;
    submittingRef.current = true;
    setBusy('submit');
    setError(null);
    try {
      const result = await repository.createAdjustment(
        wallet.id,
        { ...normalize(form), previewHash: preview.previewHash },
        idempotencyKeyRef.current,
      );
      setApproval(result.data);
      idempotencyKeyRef.current = crypto.randomUUID();
    } catch (cause) {
      setError(toSafeError(cause, t('wallets.adjustment.errors.submit')));
      submittingRef.current = false;
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog
      aria-describedby="wallet-adjustment-description"
      fullWidth
      maxWidth="sm"
      onClose={close}
      open={open}
    >
      <DialogTitle>{t('wallets.adjustment.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <DataSourceBadge source={dataSource} />
          <DialogContentText id="wallet-adjustment-description">
            {t('wallets.adjustment.description', { wallet: wallet.id })}
          </DialogContentText>
          {approval === null ? (
            <>
              <TextField
                autoFocus
                disabled={busy !== null}
                error={form.signedAmount.length > 0 && !isValidAmount(form.signedAmount)}
                helperText={t('wallets.adjustment.amountHelp')}
                label={t('wallets.adjustment.signedAmount')}
                onChange={(event) => update('signedAmount', event.target.value)}
                required
                slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                value={form.signedAmount}
              />
              <TextField
                disabled={busy !== null}
                label={t('wallets.adjustment.reasonCode')}
                onChange={(event) => update('reasonCode', event.target.value)}
                required
                select
                value={form.reasonCode}
              >
                <MenuItem value="CORRECTION">CORRECTION</MenuItem>
                <MenuItem value="MANUAL_CREDIT">MANUAL_CREDIT</MenuItem>
                <MenuItem value="MANUAL_DEBIT">MANUAL_DEBIT</MenuItem>
                <MenuItem value="REVERSAL_CORRECTION">REVERSAL_CORRECTION</MenuItem>
              </TextField>
              <TextField
                disabled={busy !== null}
                label={t('wallets.adjustment.evidenceReference')}
                onChange={(event) => update('evidenceReference', event.target.value)}
                required
                slotProps={{ htmlInput: { maxLength: 1000 } }}
                value={form.evidenceReference}
              />
              <TextField
                disabled={busy !== null}
                label={t('wallets.adjustment.reason')}
                minRows={2}
                multiline
                onChange={(event) => update('reason', event.target.value)}
                slotProps={{ htmlInput: { maxLength: 2000, required: true } }}
                value={form.reason}
              />
              {preview === null ? null : (
                <Paper component="section" sx={{ p: 2 }} variant="outlined">
                  <Typography component="h3" sx={{ mb: 1 }} variant="h6">
                    {t('wallets.adjustment.impact')}
                  </Typography>
                  <MetadataList
                    items={[
                      {
                        label: t('wallets.adjustment.availableBefore'),
                        value: exactMoney(preview.availableBefore, preview.currencyCode),
                      },
                      {
                        label: t('wallets.adjustment.delta'),
                        value: exactMoney(preview.signedAmount, preview.currencyCode),
                      },
                      {
                        label: t('wallets.adjustment.availableAfter'),
                        value: exactMoney(preview.availableAfter, preview.currencyCode),
                      },
                      {
                        label: t('wallets.adjustment.reservedAndTotalAfter'),
                        value: t('wallets.adjustment.notProvided'),
                      },
                    ]}
                  />
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {t('wallets.adjustment.approvalRequired')}
                  </Alert>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={confirmed}
                        onChange={(event) => setConfirmed(event.target.checked)}
                      />
                    }
                    label={t('wallets.adjustment.confirmImpact')}
                  />
                </Paper>
              )}
            </>
          ) : (
            <Alert severity="success">
              <Stack spacing={1}>
                <span>{t('wallets.adjustment.approvalCreated', { status: approval.status })}</span>
                <span>{approval.id}</span>
                {canViewApproval ? (
                  <MuiLink
                    component={Link}
                    to={`${paths.approvals}/${encodeURIComponent(approval.id)}`}
                  >
                    {t('wallets.adjustment.viewApproval')}
                  </MuiLink>
                ) : null}
              </Stack>
            </Alert>
          )}
          {error === null ? null : (
            <Alert severity="error">
              {error.message}
              {error.requestId === null ? null : ` Request ID: ${error.requestId}`}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy !== null} onClick={close}>
          {t('actions.close')}
        </Button>
        {approval !== null ? null : preview === null ? (
          <Button
            disabled={!valid || busy !== null}
            loading={busy === 'preview'}
            onClick={() => void previewAdjustment()}
          >
            {t('wallets.adjustment.preview')}
          </Button>
        ) : (
          <Button
            disabled={!confirmed || busy !== null}
            loading={busy === 'submit'}
            onClick={() => void submitAdjustment()}
            variant="contained"
          >
            {t('wallets.adjustment.submit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function isValid(form: AdjustmentForm) {
  return (
    isValidAmount(form.signedAmount) &&
    reasonCodePattern.test(form.reasonCode) &&
    form.reason.trim().length > 0 &&
    form.reason.trim().length <= 2000 &&
    form.evidenceReference.trim().length > 0 &&
    form.evidenceReference.trim().length <= 1000
  );
}

function isValidAmount(value: string) {
  const normalized = value.trim();
  return signedAmountPattern.test(normalized) && compareWalletDecimals(normalized, '0') !== 0;
}

function normalize(form: AdjustmentForm): WalletAdjustmentInput {
  return {
    signedAmount: form.signedAmount.trim(),
    reasonCode: form.reasonCode,
    evidenceReference: form.evidenceReference.trim(),
    reason: form.reason.trim(),
  };
}

function exactMoney(amount: string, currencyCode: string) {
  const formatted = formatDecimalString(amount);
  const currency = currencyCode.trim().toUpperCase();
  return formatted.startsWith('-')
    ? `-${currency}\u00a0${formatted.slice(1)}`
    : `${currency}\u00a0${formatted}`;
}

function toSafeError(cause: unknown, message: string): AppError {
  return {
    status: isAppError(cause) ? cause.status : null,
    code: isAppError(cause) ? cause.code : 'WALLET_ADJUSTMENT_FAILED',
    message,
    requestId: isAppError(cause) ? cause.requestId : null,
    fieldErrors: {},
    retryAfterSeconds: isAppError(cause) ? cause.retryAfterSeconds : null,
  };
}

function isAppError(value: unknown): value is AppError {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Partial<AppError>).code === 'string' &&
    'requestId' in value
  );
}
